import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useRequest } from '../../hooks/UseRequest'
import Loading from '../../components/Loading'
import LayoutSidebar from "../../components/LayoutSidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretRight,
  faCaretDown,
  faBuilding,
  faCreditCard,
  faChartLine,
  faExclamationTriangle,
  faArrowDownAZ,
  faArrowUpZA,
  faArrowDownUpAcrossLine,
  faInfo
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { useDispatch, useSelector } from 'react-redux'
import { selectUser } from '../../redux/userSlice'
import { selectDropdowns, selectDropdownOptions } from '../../redux/dropdownSlice'
import { hasUserPermission } from '../../utils/permissionHelper'
import { CurrencyFormat, DateFormat } from '../../helpers/formatting'
import { setNotification } from "../../redux/notificationSlice";
import { selectAuth } from "../../redux/authSlice";

const DecisionLogicResults = () => {
  const auth = useSelector(selectAuth)
  const [responseData, setResponseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const params = useParams();
  const requests = useRequest();
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
  const user = useSelector(selectUser)
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const navi = useNavigate()
  const dispatch = useDispatch();
  const [bankChange, setBankChange] = useState(null)
  const [managerApproval, setManagerApproval] = useState(null)
  const [customer, setCustomer] = useState(null)

  // Sorting state for tables
  const [transactionAnalysisSort, setTransactionAnalysisSort] = useState({ column: null, direction: 'asc' });
  const [expenseBreakdownSort, setExpenseBreakdownSort] = useState({ column: null, direction: 'asc' });
  const [transactionHistorySort, setTransactionHistorySort] = useState({ column: null, direction: 'asc' });

  // Collapsible state for each table
  const [expandedTransactionAnalysis, setExpandedTransactionAnalysis] = useState(true);
  const [expandedExpenseBreakdown, setExpandedExpenseBreakdown] = useState(true);
  const [expandedTransactionHistory, setExpandedTransactionHistory] = useState(true);

  // Bank change form state
  const [bankChangeStatus, setBankChangeStatus] = useState('pending');
  const [bankChangeReason, setBankChangeReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Derive customer display data prioritizing bankChange fields, fallback to API `customer`
  const customerData = {
    name: bankChange?.customer?.label || (customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : ''),
    emailAddress: bankChange?.customer?.email_address || customer?.email_address || '',
    // Only available from bankChange; if absent, do not compare/highlight
    routingNumber: bankChange?.new_bank_account?.bank_routing_number?.number || '',
    accountNumber: bankChange?.new_bank_account?.bank_account_number_last_four || ''
  }
  const oldAccountLastFour = bankChange?.old_bank_account?.bank_account_number_last_four || ''
  const oldAccountRouting = bankChange?.old_bank_account?.bank_routing_number?.number || ''

  // Generic helper function to find dropdown option by code_key or id
  const findDropdownOption = (searchValue, searchBy = 'code_key') => {
    const option = dropdownOptions.filter((o) => o[searchBy] === searchValue)[0];
    return option || null;
  };

  // Helper function to derive bank change status from manager approval
  const getBankChangeStatusFromManagerApproval = () => {
    if (!managerApproval?.mgr_approval_status_dropdown_option_id) return 'pending';

    const statusOption = findDropdownOption(managerApproval.mgr_approval_status_dropdown_option_id, 'id');
    if (!statusOption) return 'pending';

    switch (statusOption.code_key) {
      case 'mgr_approval_status_approved':
        return 'approved';
      case 'mgr_approval_status_declined':
        return 'denied';
      case 'mgr_approval_status_required':
        return 'pending';
      default:
        return 'pending';
    }
  };

  // Helper function to check if data matches (compare UI value against expected new account/customer values)
  const checkDataMatch = (field, expectedValue, actualValue) => {
    if (!actualValue) return false;

    switch (field) {
      case 'email':
        return expectedValue ? actualValue.toLowerCase() === expectedValue.toLowerCase() : false;
      case 'accountNumber':
        // Check if last 4 digits match
        if (!expectedValue) return false;
        const lastFourActual = actualValue.slice(-4);
        const lastFourExpected = expectedValue.slice(-4);
        return lastFourActual === lastFourExpected;
      case 'routingNumber':
        return expectedValue ? actualValue === expectedValue : false;
      case 'name':
        // Case-insensitive name comparison
        return expectedValue ? actualValue.toLowerCase() === expectedValue.toLowerCase() : false;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (user.status === 'succeeded' && user.data.user_permission_options) {
      const activePermissions = user.spoofed_user?.user_permission_options ? user.spoofed_user.user_permission_options : user.data.user_permission_options
      if (!hasUserPermission(activePermissions, `can_view_bank_login_results`)) {
        navi('/403')
      }
    }
  }, [user])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      const id = params?.id
      if (!id) {
        setErrorMessage('Missing id parameter')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setErrorMessage('')
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${id}/decision_logic_report`)
      requests.get()
        .then((res) => {
          const apiData = res?.data?.data
          // Expect keys: report, bank_change, manager_approval, customer
          setBankChange(apiData?.bank_change || null)
          setManagerApproval(apiData?.manager_approval || null)
          setCustomer(apiData?.customer || null)
          if (apiData?.report) {
            setResponseData(apiData.report)
          } else {
            setResponseData(null)
            setErrorMessage('Response data not available')
          }
        })
        .catch((e) => {
          setResponseData(null)
          setErrorMessage('Response data not available')
        })
        .finally(() => setIsLoading(false))
    }
  }, [params?.id, auth.accessToken])

  const toggleTransactionAnalysis = () => {
    setExpandedTransactionAnalysis((prev) => !prev);
  };

  const toggleExpenseBreakdown = () => {
    setExpandedExpenseBreakdown((prev) => !prev);
  };

  const toggleTransactionHistory = () => {
    setExpandedTransactionHistory((prev) => !prev);
  };

  const handleAccountChange = (selectedOption) => {
    setSelectedAccountIndex(selectedOption.value);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Determine the outcome dropdown option ID based on the selected status
      const codeKey = bankChangeStatus === 'approved'
        ? 'bank_transaction_provider_call_outcome_approved'
        : 'bank_transaction_provider_call_outcome_declined';

      const outcomeDropdownOptionId = findDropdownOption(codeKey, 'code_key')?.id;

      // Prepare the PATCH request data
      const patchData = {
        outcome_dropdown_option_id: outcomeDropdownOptionId,
        description: bankChangeReason.trim()
      };

      // Make the PATCH request
      const managerApprovalId = managerApproval?.id;

      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${params.id}/decision_logic_manager_decision/${managerApprovalId}`)
      const response = await requests.modify(patchData);

      // Handle response
      if (response.data.success) {
        // Update manager approval with new data
        setManagerApproval(response.data.data.manager_approval);
        // Reset form
        setBankChangeStatus('pending');
        setBankChangeReason('');
      } else {
        dispatch(setNotification({ type: 'error', body: response?.data?.message }))
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error }))
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sorting helper function
  const handleSort = (setSortState, column, data) => {
    setSortState((prevSort) => {
      const newDirection = prevSort.column === column && prevSort.direction === 'asc' ? 'desc' : 'asc';
      return { column, direction: newDirection };
    });
  };

  const sortData = (data, column, direction) => {
    if (!column || !data || data.length === 0) return data;

    // Create a completely new array with new objects to avoid any mutation
    const sortedData = data.map((item) => ({ ...item })).sort((a, b) => {
      const aVal = a[column];
      const bVal = b[column];

      // Special handling for score column with N/A values
      if (column === 'score') {
        const aIsNA = aVal === "N/A" || aVal === "" || aVal === null;
        const bIsNA = bVal === "N/A" || bVal === "" || bVal === null;

        // If both are N/A, they're equal
        if (aIsNA && bIsNA) return 0;

        // If only one is N/A, put N/A at bottom when descending
        if (aIsNA && !bIsNA) return direction === 'desc' ? 1 : -1;
        if (!aIsNA && bIsNA) return direction === 'desc' ? -1 : 1;

        // Both are valid values, sort normally
        const aNum = Number(aVal);
        const bNum = Number(bVal);

        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // Fallback to string comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      }

      // Convert to numbers if they look like numbers
      const aNum = Number(aVal);
      const bNum = Number(bVal);

      // If both are valid numbers, sort numerically
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Otherwise sort as strings
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    return sortedData;
  };

  const getSortIcon = (column, sortState) => {
    if (sortState.column !== column) return faArrowDownUpAcrossLine;
    return sortState.direction === 'asc' ? faArrowDownAZ : faArrowUpZA;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 3: return 'has-text-success';
      case 2: return 'has-text-warning';
      case 1: return 'has-text-info';
      default: return 'has-text-danger';
    }
  };

  const getStatusClass = (status) => {
    if (status === 'approved') return 'is-success';
    if (status === 'denied') return 'is-danger';
    if (status === 'pending') return 'is-warning';
    return 'is-light';
  };

  if (isLoading) {
    return (
      <LayoutSidebar
        contentTitle="Decision Logic Results"
        actionButtons={customer?.id ? (
          <button
            type="button"
            className="button has-background-grey has-text-white is-small header-button"
            onClick={() => {
              navi(`/customer/${customer.id}`)
            }}
          >
            <span className="mx-3">Customer Details</span>
          </button>
        ) : null}
        content={(
          <div className="p-4"><Loading /></div>
        )}
      />
    );
  }

  // Only check if responseData exists and is not an empty object
  if (!responseData || (typeof responseData === 'object' && Object.keys(responseData).length === 0)) {
    return (
      <LayoutSidebar
        contentTitle="Decision Logic Results"
        actionButtons={customer?.id ? (
          <button
            type="button"
            className="button has-background-grey has-text-white is-small header-button"
            onClick={() => {
              navi(`/customer/${customer.id}`)
            }}
          >
            <span className="mx-3">Customer Details</span>
          </button>
        ) : null}
        content={(
          <div className="has-text-centered p-6">
            <p className="mt-3">{errorMessage || 'Response data not available'}</p>
          </div>
        )}
      />
    );
  }

  const { reportsDetails } = responseData || {};
  const selectedAccount = reportsDetails?.[selectedAccountIndex];

  // Precomputed booleans to keep JSX simple (single ternaries only)
  const nameExpected = !!customerData.name;
  const nameMatch = nameExpected && checkDataMatch('name', customerData.name, selectedAccount?.nameFound);
  const emailExpected = !!customerData.emailAddress;
  const emailMatch = emailExpected && checkDataMatch('email', customerData.emailAddress, selectedAccount?.emailAddress);
  const routingExpected = !!customerData.routingNumber;
  const routingMatch = routingExpected && checkDataMatch('routingNumber', customerData.routingNumber, selectedAccount?.routingNumberEntered);
  const accountMatch = !!bankChange && checkDataMatch('accountNumber', customerData.accountNumber, selectedAccount?.accountNumberFound);

  // Create dropdown options
  const accountOptions = reportsDetails?.map((account, index) => ({
    value: index,
    label: `${account.institutionName} - ${account.accountName} - ${account.accountNumberFound} (${account.accountType})`,
    account: account
  })) || [];

  // Check if user has manager_approval permissions
  const activePermissions = user.spoofed_user?.user_permission_options ? user.spoofed_user.user_permission_options : user.data?.user_permission_options
  const hasManagerApprovalReadPermission = hasUserPermission(activePermissions, 'manager_approval_read')
  const hasManagerApprovalUpdatePermission = hasUserPermission(activePermissions, 'manager_approval_update')

  return (
    <LayoutSidebar
      contentTitle="Decision Logic Results"
      actionButtons={customer?.id ? (
        <button
          type="button"
          className="button has-background-grey has-text-white is-small header-button"
          onClick={() => {
            navi(`/customer/${customer.id}`)
          }}
        >
          <span className="mx-3">Customer Details</span>
        </button>
      ) : null}
      content={(
        <div className="lox-detail mt-3">
          {selectedAccount && (
            <>
              {bankChange && hasManagerApprovalReadPermission && (
                <div className="box mb-3 ml-2 mr-2 pt-4 pb-2">
                  <div className="columns mb-0">
                    <div className="column is-12">
                      <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
                        <h4 className="title is-6 mb-0">
                          <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                          Bank Change Manager Approval
                        </h4>
                        <div className="is-flex is-align-items-center">
                          <span className="has-text-weight-bold mr-2">STATUS:</span>
                          <span
                            className={`tag is-large has-text-weight-bold ${getStatusClass(getBankChangeStatusFromManagerApproval())}`}
                          >
                            {getBankChangeStatusFromManagerApproval() ? getBankChangeStatusFromManagerApproval().charAt(0).toUpperCase() + getBankChangeStatusFromManagerApproval().slice(1) : 'Not Set'}
                          </span>
                        </div>
                      </div>
                      <div className="content is-small mb-1">
                        <div className="columns mb-0">
                          <div className="column is-3">
                            <p className="mb-1"><strong>Customer:</strong> {customerData.name}</p>
                          </div>
                          <div className="column is-3">
                            <p className="mb-1"><strong>Email:</strong> <span className="lox-break-anywhere">{customerData.emailAddress}</span></p>
                          </div>
                          <div className="column is-3">
                            <p className="mb-1"><strong>New Account:</strong> ****{customerData.accountNumber} / {customerData.routingNumber}</p>
                          </div>
                          <div className="column is-3">
                            <p className="mb-1"><strong>Old Account:</strong> ****{oldAccountLastFour} / {oldAccountRouting}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Section */}
              <div className="box mb-4 ml-2 mr-2">
                <div className="columns">
                  {/* Left 50% - Account Information + Balance Analysis */}
                  <div className="column is-6">
                    <div className="columns mb-0">
                      <div className="column is-6">
                        <h4 className="title is-6 mb-3">
                          <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                          Account Information
                        </h4>
                        <div className="content is-small">
                          <p className={`${nameExpected && (nameMatch ? 'has-background-success-light p-1' : 'has-background-danger-light p-1')}`}>
                            <strong>Customer:</strong> {selectedAccount.nameFound}
                            {customerData.name && (
                              <span
                                className="lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
                                data-tooltip={`Customer: ${customerData.name}`}
                              >
                                <FontAwesomeIcon icon={faInfo} className="has-text-link" />
                              </span>
                            )}
                          </p>
                          <p className={`${emailExpected && (emailMatch ? 'has-background-success-light p-1' : 'has-background-danger-light p-1')}`}>
                            <strong>Email:</strong> {selectedAccount.emailAddress}
                            {customerData.emailAddress && (
                              <span
                                className="lox-no-border ml-1 lox-tooltip-wide has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
                                data-tooltip={`Customer: ${customerData.emailAddress}`}
                              >
                                <FontAwesomeIcon icon={faInfo} className="has-text-link" />
                              </span>
                            )}
                          </p>
                          <p className={`${routingExpected && (routingMatch ? 'has-background-success-light p-1' : 'has-background-danger-light p-1')}`}>
                            <strong>Routing Number:</strong> {selectedAccount.routingNumberEntered}
                            {customerData.routingNumber && (
                              <span
                                className="lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
                                data-tooltip={`Customer: ${customerData.routingNumber}`}
                              >
                                <FontAwesomeIcon icon={faInfo} className="has-text-link" />
                              </span>
                            )}
                          </p>
                          <p><strong>Account Confidence:</strong> {selectedAccount.accountNumberConfidence}/5</p>
                          <p><strong>Name Confidence:</strong> {selectedAccount.nameConfidence}/5</p>
                          <p><strong>Analysis Date:</strong> {DateFormat({ dateString: selectedAccount.asOfDate })}</p>
                        </div>
                      </div>
                      <div className="column is-6">
                        <h4 className="title is-6 mb-3">
                          <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                          Balance Analysis
                        </h4>
                        <div className="content is-small">
                          <p><strong>Available Balance:</strong> {CurrencyFormat({ value: selectedAccount.availableBalance })}</p>
                          <p><strong>Average Balance:</strong> {CurrencyFormat({ value: selectedAccount.averageBalance })}</p>
                          <p><strong>Recent Average:</strong> {CurrencyFormat({ value: selectedAccount.averageBalanceRecent })}</p>
                          <p><strong>Total Credits:</strong> {CurrencyFormat({ value: selectedAccount.totalCredits })}</p>
                          <p><strong>Total Debits:</strong> {CurrencyFormat({ value: selectedAccount.totalDebits })}</p>
                          <p><strong>Activity Period:</strong> {DateFormat({ dateString: selectedAccount.activityStartDate })} - {DateFormat({ dateString: selectedAccount.activityEndDate })}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right 50% - Account Selection */}
                  <div className="column is-6">
                    <div className="is-flex is-align-items-center mb-4">
                      <FontAwesomeIcon icon={faBuilding} className="mr-2 has-text-info" />
                      <div>
                        <h3 className="title is-4 mb-1">
                          {selectedAccount.institutionName} - {selectedAccount.accountName}
                        </h3>
                        <p className="subtitle is-6 has-text-grey mt-1">
                          {selectedAccount.accountType} •
                          <span className={accountMatch ? 'has-background-success-light p-1 ml-1' : 'has-background-danger-light p-1 ml-1'}>
                            {selectedAccount.accountNumberFound}
                            {customerData.accountNumber && (
                              <span
                                className="lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary"
                                data-tooltip={`Customer last 4: ${customerData.accountNumber}`}
                              >
                                <FontAwesomeIcon icon={faInfo} className="has-text-link" />
                              </span>
                            )}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="title is-3">{CurrencyFormat({ value: selectedAccount.currentBalance })}</p>
                    </div>

                    <label className="label">Select Account to Analyze</label>
                    <Select
                      options={accountOptions}
                      value={accountOptions[selectedAccountIndex]}
                      onChange={handleAccountChange}
                      isSearchable={false}
                      placeholder="Choose an account..."
                      styles={{
                        menu: ({ ...css }) => ({ ...css, width: '100%', zIndex: 20 })
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Tables Section */}
              <div className="box mb-4 ml-2 mr-2">
                {/* Transaction Analysis */}
                <div
                  className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0 is-clickable"
                  onClick={toggleTransactionAnalysis}
                >
                  <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">
                    <span className="mr-2">
                      {expandedTransactionAnalysis ? <FontAwesomeIcon icon={faCaretDown} className="ml-0" /> : <FontAwesomeIcon icon={faCaretRight} className="ml-0" />}
                    </span>
                    Transaction Analysis ({selectedAccount.transactionAnalysisSummaries.length} items)
                  </p>
                </div>

                {expandedTransactionAnalysis && (
                  <div className="list-table-container">
                    <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
                      <thead>
                        <tr className="is-selected">
                          <th className="is-selected has-text-centered is-nowrap">
                            Type
                            <span className="is-clickable pl-1">
                              <FontAwesomeIcon
                                icon={getSortIcon('typeName', transactionAnalysisSort)}
                                onClick={() => handleSort(setTransactionAnalysisSort, 'typeName', selectedAccount.transactionAnalysisSummaries)}
                              />
                            </span>
                          </th>
                          <th className="is-selected has-text-centered is-nowrap">
                            Total Count
                            <span className="is-clickable pl-1">
                              <FontAwesomeIcon
                                icon={getSortIcon('totalCount', transactionAnalysisSort)}
                                onClick={() => handleSort(setTransactionAnalysisSort, 'totalCount', selectedAccount.transactionAnalysisSummaries)}
                              />
                            </span>
                          </th>
                          <th className="is-selected has-text-centered is-nowrap">
                            Total Amount
                            <span className="is-clickable pl-1">
                              <FontAwesomeIcon
                                icon={getSortIcon('totalAmount', transactionAnalysisSort)}
                                onClick={() => handleSort(setTransactionAnalysisSort, 'totalAmount', selectedAccount.transactionAnalysisSummaries)}
                              />
                            </span>
                          </th>
                          <th className="is-selected has-text-centered is-nowrap">
                            Recent Count
                            <span className="is-clickable pl-1">
                              <FontAwesomeIcon
                                icon={getSortIcon('recentCount', transactionAnalysisSort)}
                                onClick={() => handleSort(setTransactionAnalysisSort, 'recentCount', selectedAccount.transactionAnalysisSummaries)}
                              />
                            </span>
                          </th>
                          <th className="is-selected has-text-centered is-nowrap">
                            Recent Amount
                            <span className="is-clickable pl-1">
                              <FontAwesomeIcon
                                icon={getSortIcon('recentAmount', transactionAnalysisSort)}
                                onClick={() => handleSort(setTransactionAnalysisSort, 'recentAmount', selectedAccount.transactionAnalysisSummaries)}
                              />
                            </span>
                          </th>
                          <th className="is-selected has-text-centered is-nowrap">
                            Score
                            <span className="is-clickable pl-1">
                              <FontAwesomeIcon
                                icon={getSortIcon('score', transactionAnalysisSort)}
                                onClick={() => handleSort(setTransactionAnalysisSort, 'score', selectedAccount.transactionAnalysisSummaries)}
                              />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortData(
                          selectedAccount.transactionAnalysisSummaries,
                          transactionAnalysisSort.column,
                          transactionAnalysisSort.direction
                        ).map((summary) => (
                          <tr key={`analysis-${selectedAccount.requestCode}-${summary.typeCode}-${summary.displayOrder || Math.random()}`}>
                            <td className="has-text-centered is-nowrap">
                              {summary.typeName}
                              {summary.message && (
                                <span
                                  className="lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
                                  data-tooltip={summary.message}
                                >
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="has-text-warning" />
                                </span>
                              )}
                            </td>
                            <td className="has-text-centered is-nowrap">
                              {summary.totalCount}
                            </td>
                            <td className="has-text-centered is-nowrap">
                              {CurrencyFormat({ value: summary.totalAmount })}
                            </td>
                            <td className="has-text-centered is-nowrap">
                              {summary.recentCount}
                            </td>
                            <td className="has-text-centered is-nowrap">
                              {CurrencyFormat({ value: summary.recentAmount })}
                            </td>
                            <td className="has-text-centered is-nowrap">
                              {summary.score === "N/A" || summary.score === "" ? (
                                <span className="has-text-grey">N/A</span>
                              ) : (
                                <span className="has-text-success">{summary.score}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Transaction History */}
                {selectedAccount.transactionSummaries && selectedAccount.transactionSummaries.length > 0 && (
                  <>
                    <div
                      className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0 mt-3 is-clickable"
                      onClick={toggleTransactionHistory}
                    >
                      <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">
                        <span className="mr-2">
                          {expandedTransactionHistory ? <FontAwesomeIcon icon={faCaretDown} className="ml-0" /> : <FontAwesomeIcon icon={faCaretRight} className="ml-0" />}
                        </span>
                        Transaction History ({selectedAccount.transactionSummaries.length} transactions)
                      </p>
                    </div>

                    {expandedTransactionHistory && (
                    <div className="list-table-container lox-scroll-400">
                      <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
                        <thead className="is-sticky lox-sticky-header">
                          <tr className="is-selected">
                            <th className="is-selected has-text-centered is-nowrap">
                              Date
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('transactionDate', transactionHistorySort)}
                                  onClick={() => handleSort(setTransactionHistorySort, 'transactionDate', selectedAccount.transactionSummaries)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Description
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('description', transactionHistorySort)}
                                  onClick={() => handleSort(setTransactionHistorySort, 'description', selectedAccount.transactionSummaries)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Amount
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('amount', transactionHistorySort)}
                                  onClick={() => handleSort(setTransactionHistorySort, 'amount', selectedAccount.transactionSummaries)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Balance
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('runningBalance', transactionHistorySort)}
                                  onClick={() => handleSort(setTransactionHistorySort, 'runningBalance', selectedAccount.transactionSummaries)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Category
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('category', transactionHistorySort)}
                                  onClick={() => handleSort(setTransactionHistorySort, 'category', selectedAccount.transactionSummaries)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Status
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('status', transactionHistorySort)}
                                  onClick={() => handleSort(setTransactionHistorySort, 'status', selectedAccount.transactionSummaries)}
                                />
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortData(
                            selectedAccount.transactionSummaries,
                            transactionHistorySort.column,
                            transactionHistorySort.direction
                          ).map((transaction) => (
                            <tr key={`transaction-${selectedAccount.requestCode}-${transaction.transactionDate}-${transaction.description}-${transaction.amount}`}>
                              <td className="has-text-centered is-nowrap">
                                {DateFormat({ dateString: transaction.transactionDate })}
                              </td>
                              <td className="has-text-centered">
                                <span className={transaction.description.toLowerCase().includes('deposit') && transaction.amount > 0 ? 'has-text-weight-bold' : ''}>
                                  {transaction.description}
                                </span>
                              </td>
                              <td className={`has-text-centered ${transaction.amount >= 0 ? 'has-text-success' : 'has-text-danger'} is-nowrap`}>
                                {CurrencyFormat({ value: transaction.amount })}
                              </td>
                              <td className="has-text-centered is-nowrap">
                                {CurrencyFormat({ value: transaction.runningBalance })}
                              </td>
                              <td className="has-text-centered is-nowrap">
                                <span className="tag is-light">{transaction.category}</span>
                              </td>
                              <td className="has-text-centered is-nowrap">
                                <span className={`tag ${transaction.status === 'POSTED' ? 'is-success' : 'is-warning'}`}>
                                  {transaction.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </>
                )}

                {/* Expense Breakdown */}
                {selectedAccount.accountExpenses && selectedAccount.accountExpenses.length > 0 && (
                  <>
                    <div
                      className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0 mt-3 is-clickable"
                      onClick={toggleExpenseBreakdown}
                    >
                      <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">
                        <span className="mr-2">
                          {expandedExpenseBreakdown ? <FontAwesomeIcon icon={faCaretDown} className="ml-0" /> : <FontAwesomeIcon icon={faCaretRight} className="ml-0" />}
                        </span>
                        Expense Breakdown ({selectedAccount.accountExpenses.length} items)
                      </p>
                    </div>

                    {expandedExpenseBreakdown && (
                    <div className="list-table-container">
                      <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
                        <thead>
                          <tr className="is-selected">
                            <th className="is-selected has-text-centered is-nowrap">
                              Category
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('category', expenseBreakdownSort)}
                                  onClick={() => handleSort(setExpenseBreakdownSort, 'category', selectedAccount.accountExpenses)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Amount
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('amount', expenseBreakdownSort)}
                                  onClick={() => handleSort(setExpenseBreakdownSort, 'amount', selectedAccount.accountExpenses)}
                                />
                              </span>
                            </th>
                            <th className="is-selected has-text-centered is-nowrap">
                              Percentage
                              <span className="is-clickable pl-1">
                                <FontAwesomeIcon
                                  icon={getSortIcon('percent', expenseBreakdownSort)}
                                  onClick={() => handleSort(setExpenseBreakdownSort, 'percent', selectedAccount.accountExpenses)}
                                />
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortData(
                            selectedAccount.accountExpenses,
                            expenseBreakdownSort.column,
                            expenseBreakdownSort.direction
                          ).map((expense) => (
                            <tr key={`expense-${selectedAccount.requestCode}-${expense.category}-${expense.amount}`}>
                              <td className="has-text-centered is-nowrap">
                                {expense.category}
                              </td>
                              <td className="has-text-centered is-nowrap">
                                {CurrencyFormat({ value: expense.amount })}
                              </td>
                              <td className="has-text-centered is-nowrap">
                                {expense.percent.toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </>
                )}
              </div>

              {/* Bank Change Information Section */}
              {managerApproval && bankChange && hasManagerApprovalUpdatePermission && (
                <div className="box mb-3 ml-2 mr-2 lox-max-width-720">
                  <div className="columns mb-0">
                    <div className="column is-12">
                      <h4 className="title is-6 mb-2">
                        <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                        Bank Change Information
                      </h4>
                      <div className="content is-small mb-3">
                        <p className="mb-1"><strong>Customer:</strong> {customerData.name}</p>
                        <p className="mb-1"><strong>Email:</strong> <span className="lox-break-anywhere">{customerData.emailAddress}</span></p>
                        <p className="mb-1"><strong>New Account:</strong> ****{customerData.accountNumber} / {customerData.routingNumber}</p>
                        <p className="mb-1"><strong>Old Account:</strong> ****{oldAccountLastFour} / {oldAccountRouting}</p>
                      </div>
                      {getBankChangeStatusFromManagerApproval() === 'pending' && (
                        <>
                          <div className="field mb-2 lox-max-width-420">
                            <label className="label is-small mb-1">Status</label>
                            <div className="control is-flex is-align-items-center">
                              <label className="radio mr-3">
                                <input
                                  type="radio"
                                  name="bankChangeStatus"
                                  value="approved"
                                  checked={bankChangeStatus === 'approved'}
                                  onChange={(e) => setBankChangeStatus(e.target.value)}
                                />
                                <span className="ml-1">Approve</span>
                              </label>
                              <label className="radio">
                                <input
                                  type="radio"
                                  name="bankChangeStatus"
                                  value="denied"
                                  checked={bankChangeStatus === 'denied'}
                                  onChange={(e) => setBankChangeStatus(e.target.value)}
                                />
                                <span className="ml-1">Deny</span>
                              </label>
                            </div>
                          </div>
                          <div className="field mb-3 lox-max-width-720">
                            <label className="label is-small mb-1">Reason</label>
                            <div className="control">
                              <textarea
                                className="textarea is-small"
                                placeholder="Add reason..."
                                value={bankChangeReason}
                                onChange={(e) => setBankChangeReason(e.target.value)}
                                rows={3}
                              />
                            </div>
                          </div>
                          <div className="field">
                            <div className="control">
                              <button
                                className="button is-primary is-small"
                                type="button"
                                disabled={isSubmitting || bankChangeStatus === 'pending' || !findDropdownOption(bankChangeStatus === 'approved' ? 'bank_transaction_provider_call_outcome_approved' : 'bank_transaction_provider_call_outcome_declined', 'code_key')?.id || !managerApproval?.id}
                                onClick={handleSubmit}
                              >
                                {isSubmitting ? (
                                  <>
                                    <Loading size="is-size-7" noWrapper />
                                    <span className="ml-2">Submitting</span>
                                  </>
                                ) : (
                                  'Submit Bank Change'
                                )}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    />
  )
}

export default DecisionLogicResults
